<div class="checkout-page">
    <div class="checkout-topbar">
        <button type="button" class="back-pill" *ngIf="!submitSucceeded" (click)="goBack()">
            <i class="pi pi-chevron-left"></i> Back
        </button>
        <span class="topbar-title">Checkout</span>
        <img class="topbar-logo" [src]="_global.whiteLabel?.logo" [alt]="_global.whiteLabel?.wlName || 'Logo'" onerror="this.style.display='none'" />
        <button type="button" class="close-pill" *ngIf="!submitSucceeded && !showAddCardStep" (click)="requestExit()" aria-label="Exit checkout" title="Exit checkout">
            <i class="pi pi-times"></i>
        </button>
    </div>

    <ng-container *ngIf="!isLoading; else pageSkeleton">

    <div class="checkout-body checkout-usage-result" *ngIf="showAddCardStep">
        <div class="usage-result-card">
            <i class="pi pi-credit-card usage-result-icon"></i>
            <ng-container *ngIf="awaitingCardForPrecheck; else optionalCardCopy">
                <h2>Complete Your Payment</h2>
                <p>Review your payment details below and complete today's payment to continue with your hardware purchase.</p>
                <div class="usage-result-charge-breakdown">
                    <div class="charge-breakdown-row">
                        <span>Charged today</span>
                        <strong>{{ grandTotal | currency | currencySpacing }}</strong>
                    </div>
                    <!-- Hardware is always charged the moment a card is on file — never deferred.
                         The recurring subscription (plan/registers/locations/add-ons) always defers
                         to the next billing cycle instead, regardless of the account's own trial
                         status — see subtotal's doc comment. -->
                    <div class="charge-breakdown-line-item">
                        <span>Hardware (one-time)</span>
                        <span>{{ hardwareTotal | currency | currencySpacing }}</span>
                    </div>
                    <div class="charge-breakdown-line-item">
                        <span>Tax</span>
                        <span>{{ taxAmount | currency | currencySpacing }}</span>
                    </div>
                    <div class="charge-breakdown-note">Charged as soon as you add a card</div>
                    <ng-container *ngIf="hasAnyUsageSelection && recurringTotalDisplayWithTax > 0 && !isHardwareOnlyPurchase">
                        <div class="charge-breakdown-row">
                            <span>Billed {{ isFreeTrialAccount ? ('starting ' + (freeTrialEndDate | date : 'MMM d, yyyy')) : 'next cycle' }}</span>
                            <strong>{{ recurringTotalDisplayWithTax | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "yr" : "mo" }}</strong>
                        </div>
                        <div class="charge-breakdown-note">Includes tax &middot; nothing is charged for this today</div>
                    </ng-container>
                </div>
            </ng-container>
            <ng-template #optionalCardCopy>
                <h2>Add a card <span>(optional)</span></h2>
                <p>You're still in your free period, so nothing is due today. Add a card now so billing continues without interruption when your free period ends — or skip and add one later from Subscription Management.</p>
                <div class="usage-result-charge-breakdown" *ngIf="recurringTotalDisplayWithTax > 0">
                    <div class="charge-breakdown-row">
                        <span>Billed starting {{ freeTrialEndDate | date : "MMM d, yyyy" }}</span>
                        <strong>{{ recurringTotalDisplayWithTax | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "yr" : "mo" }}</strong>
                    </div>
                    <div class="charge-breakdown-note">Includes tax &middot; nothing is charged today</div>
                </div>
            </ng-template>
            <app-stripe-add-card-button [label]="'Pay ' + (grandTotal | currency | currencySpacing)" (cardSaved)="onAddCardStepSaved($event)"></app-stripe-add-card-button>
            <button type="button" class="usage-result-skip" *ngIf="!awaitingCardForPrecheck" (click)="skipAddCardStep()">Skip for now</button>
        </div>
    </div>

    <div class="checkout-body checkout-usage-result" *ngIf="submitSucceeded">
        <div class="usage-result-card">
            <i class="pi pi-check-circle usage-result-icon success"></i>
            <h2>You're all set</h2>
            <p>Here's what you added to your {{ plan?.planName }} plan this session.</p>
            <div class="usage-receipt" *ngIf="purchasedLineItems.length">
                <div class="usage-receipt-row" *ngFor="let line of purchasedLineItems">
                    <span>{{ line.name }}<ng-container *ngIf="line.qty > 1"> &times; {{ line.qty }}</ng-container></span>
                    <span>{{ line.subTotalCost | currency | currencySpacing }}</span>
                </div>
                <div class="usage-receipt-row" *ngIf="purchasedLineItemsTax > 0">
                    <span>Sales tax ({{ taxRate }}%)</span>
                    <span>{{ purchasedLineItemsTax | currency | currencySpacing }}</span>
                </div>
                <div class="usage-receipt-total">
                    <span>Total</span>
                    <span>{{ purchasedLineItemsTotal | currency | currencySpacing }}</span>
                </div>
            </div>
            <button pButton type="button" label="Back to Subscription Management" class="usage-result-cta" [disabled]="!isUserInfoRefreshed" (click)="goToSubscriptionManagement()"></button>
            <p *ngIf="!isUserInfoRefreshed" style="margin-top: 8px; font-size: 13px; opacity: 0.7;">Updating your subscription details&hellip;</p>
        </div>
    </div>

    <!-- Covers the gap between adding a card (showAddCardStep -> false) and the resumed
         confirm()/chargeHardware() call actually finishing — without this, the full interactive
         cart (steppers, add-on checkboxes, coupon field) would flash back onto the screen for
         however long that follow-up save takes, letting the merchant edit a cart that's already
         mid-submit before it lands on the receipt. -->
    <div class="checkout-body checkout-usage-result" *ngIf="isSubmitting && !showAddCardStep && !submitSucceeded">
        <div class="usage-result-card">
            <i class="pi pi-spin pi-spinner usage-result-icon"></i>
            <h2>Finishing up…</h2>
            <p>Saving your order now that a card is on file.</p>
        </div>
    </div>

    <div class="checkout-body" *ngIf="!showAddCardStep && !submitSucceeded && !isSubmitting">
        <div class="checkout-main">
            <h1>{{ hardwareOnlyCheckout ? "Hardware & accessories" : "Checkout & cart" }}</h1>
            <p class="checkout-subtitle">{{ hardwareOnlyCheckout ? "Add the devices you need, then confirm your order." : "Add locations, registers, add-ons and hardware, then confirm your order." }}</p>

            <div class="selected-plan-row" *ngIf="!hardwareOnlyCheckout">
                <div class="selected-plan-left">
                    <span class="selected-plan-check"><i class="pi pi-check"></i></span>
                    <div>
                        <div class="selected-plan-label">SELECTED PLAN</div>
                        <div class="selected-plan-name"><strong>{{ plan?.planName }}</strong> <span class="selected-plan-price">· {{ (billingInterval === "Yearly" ? plan?.yearlyPrice * 12 : plan?.monthlyPrice) | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "year" : "month" }}</span></div>
                        <div class="selected-plan-switch-note" *ngIf="isSwitchingPlan">Switching from {{ currentPlanName }}</div>
                    </div>
                </div>
                <button pButton type="button" label="Change plan" class="p-button-outlined change-plan-btn" *ngIf="!isUsageOnlyCheckout" (click)="changePlan()"></button>
            </div>

            <!-- TODO re-enable: monthly/yearly toggle hidden per request -->
            <div class="interval-toggle" *ngIf="false">
                <button type="button" [class.active]="billingInterval === 'Monthly'" (click)="setBillingInterval('Monthly')">Pay Monthly</button>
                <button type="button" [class.active]="billingInterval === 'Yearly'" (click)="setBillingInterval('Yearly')">Pay Yearly</button>
            </div>

            <p class="trial-banner" *ngIf="isFreeTrialAccount && !isUsageOnlyCheckout">
                You're heading to checkout, but we won't charge any {{ plan?.planName }} subscription fee, extra registers/locations or add-ons<ng-container *ngIf="freeTrialEndDate"> until your <strong>1-month free period</strong> ends on <strong>{{ freeTrialEndDate | date : "MMM d, yyyy" }}</strong></ng-container>. Today you only pay for hardware you've selected (if any) — otherwise there's nothing to pay now.
            </p>

            <div class="section" *ngIf="!hardwareOnlyCheckout">
                <h2>Locations &amp; registers</h2>
                <p class="section-subtitle">Your plan includes {{ plan?.defaultLocation }} location and {{ plan?.defaultRegister }} register. Add more only if you need them.</p>

                <ng-container *ngIf="isLoadingUsageLimits; else countersLoaded">
                    <div class="counter-row counter-row-skeleton" *ngFor="let row of [1,2]">
                        <div class="counter-row-left">
                            <p-skeleton shape="circle" size="0.75rem"></p-skeleton>
                            <div>
                                <p-skeleton width="120px" height="0.85rem" styleClass="mb-2"></p-skeleton>
                                <p-skeleton width="180px" height="0.7rem"></p-skeleton>
                            </div>
                        </div>
                        <p-skeleton width="84px" height="1.75rem" styleClass="border-round-3xl"></p-skeleton>
                        <p-skeleton width="60px" height="0.85rem"></p-skeleton>
                    </div>
                </ng-container>
                <ng-template #countersLoaded>
                    <div class="counter-row" *ngFor="let item of registersAndLocationsList">
                        <div class="counter-row-left">
                            <span class="counter-dot"></span>
                            <div>
                                <div class="counter-title">Number of {{ item.name.toLowerCase() }}</div>
                                <div class="counter-meta">
                                    {{ item.id === 1 ? plan?.defaultRegister : plan?.defaultLocation }} included
                                    <ng-container *ngIf="alreadyPurchasedExtra(item) as ownedExtra"> · {{ ownedExtra }} already purchased</ng-container>
                                    · additional
                                    {{ (billingInterval === "Yearly" ? item.price * 12 : item.price) | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "year" : "month" }}
                                    <ng-container *ngIf="maxQuantityFor(item) as max"> · up to {{ max }}</ng-container>
                                </div>
                            </div>
                        </div>
                        <div class="counter-stepper">
                            <button type="button" (click)="decrement(item)" [disabled]="item.quantity <= (item.ownedQuantity ?? 0)" [attr.aria-label]="'Decrease number of ' + item.name.toLowerCase()"><i class="pi pi-minus"></i></button>
                            <span aria-live="polite">{{ item.quantity }}</span>
                            <button type="button" (click)="increment(item)" [disabled]="isAtMaxQuantity(item)" [attr.aria-label]="'Increase number of ' + item.name.toLowerCase()"><i class="pi pi-plus"></i></button>
                        </div>
                        <div class="counter-status" [class.extra]="item.selected">
                            <ng-container *ngIf="item.selected; else includedLabel">{{ extraCostFor(item) | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "yr" : "mo" }}</ng-container>
                            <ng-template #includedLabel>Included</ng-template>
                        </div>
                    </div>
                </ng-template>
            </div>

            <div class="section" *ngIf="!hardwareOnlyCheckout && (isLoadingUsageLimits || optionalAddons.length || alreadyPurchasedAddons.length || featuresLostOnSwitch.length)">
                <h2>Add-ons</h2>

                <div class="plan-switch-warning" *ngIf="featuresLostOnSwitch.length">
                    <i class="pi pi-exclamation-triangle"></i>
                    <div>
                        <div class="plan-switch-warning-title">Not included on {{ plan?.planName }}</div>
                        <div class="plan-switch-warning-body">
                            You currently have {{ featuresLostOnSwitch.length === 1 ? 'this' : 'these' }} on your account, but {{ plan?.planName }} doesn't include or offer {{ featuresLostOnSwitch.length === 1 ? 'it' : 'them' }} as an add-on: <strong>{{ featuresLostOnSwitchLabel }}</strong>. Switching will remove access to {{ featuresLostOnSwitch.length === 1 ? 'it' : 'them' }}.
                        </div>
                    </div>
                </div>

                <ng-container *ngIf="isLoadingUsageLimits; else addonsLoaded">
                    <div class="addon-row addon-row-skeleton" *ngFor="let row of [1,2]">
                        <p-skeleton shape="circle" size="1.5rem"></p-skeleton>
                        <div class="addon-row-text">
                            <p-skeleton width="55%" height="0.85rem" styleClass="mb-2"></p-skeleton>
                            <p-skeleton width="35%" height="0.7rem"></p-skeleton>
                        </div>
                        <p-skeleton width="64px" height="0.85rem"></p-skeleton>
                    </div>
                </ng-container>
                <ng-template #addonsLoaded>
                    <div class="addon-row addon-row-purchased" *ngFor="let addon of alreadyPurchasedAddons">
                        <p-checkbox [binary]="true" [ngModel]="true" [disabled]="true"></p-checkbox>
                        <div class="addon-row-text">
                            <div class="addon-title">{{ addon.planName }}</div>
                            <div class="addon-detail">Purchased &middot; already on your account</div>
                        </div>
                        <div class="addon-price">
                            {{ (billingInterval === "Yearly" ? addon.yearlyPrice * 12 : addon.monthlyPrice) | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "year" : "month" }}
                        </div>
                    </div>
                    <div class="addon-row" [class.selected]="addon.selected" *ngFor="let addon of optionalAddons">
                        <p-checkbox [binary]="true" [(ngModel)]="addon.selected" (onChange)="toggleAddon(addon, $event)"></p-checkbox>
                        <div class="addon-row-text">
                            <div class="addon-title">{{ addon.planName }}</div>
                            <div class="addon-detail">{{ addon.detail }}</div>
                        </div>
                        <div class="addon-price">
                            {{ (billingInterval === "Yearly" ? addon.yearlyPrice * 12 : addon.monthlyPrice) | currency | currencySpacing }}<ng-container *ngIf="addon.priceIsStartingAt">+</ng-container>/{{ billingInterval === "Yearly" ? "year" : "month" }}
                        </div>
                    </div>
                </ng-template>
            </div>

            <div class="section" *ngIf="hardwareSupported">
                <div class="section-head-row">
                    <h2>Hardware / Accessories</h2>
                    <button pButton type="button" label="Add" icon="pi pi-plus" class="add-hardware-btn" (click)="openHardwareDialog()"></button>
                </div>
                <div class="section-head-note">One-time purchase · ships in 3–5 days</div>
                <p class="section-subtitle">Optional. You can order hardware later from Settings at any time.</p>

                <div class="hardware-empty" *ngIf="!selectedHardware.length">
                    No hardware added yet. Click <strong>Add</strong> to browse terminals, readers, drawers, scanners and printers.
                </div>

                <div class="hardware-row" *ngFor="let item of selectedHardware">
                    <img class="hardware-row-img" [src]="item.image" alt="" onerror="this.style.visibility='hidden'" />
                    <div class="hardware-row-text">
                        <div class="hardware-row-title">{{ item.title }}</div>
                        <div class="hardware-row-meta">{{ item.price | currency | currencySpacing }} each</div>
                    </div>
                    <div class="counter-stepper hardware-row-stepper">
                        <button type="button" (click)="decrementSelectedHardwareQty(item)" [disabled]="item.selectedQuantity <= 1" [attr.aria-label]="'Decrease quantity of ' + item.title"><i class="pi pi-minus"></i></button>
                        <span aria-live="polite">{{ item.selectedQuantity }}</span>
                        <button type="button" (click)="incrementSelectedHardwareQty(item)" [attr.aria-label]="'Increase quantity of ' + item.title"><i class="pi pi-plus"></i></button>
                    </div>
                    <div class="hardware-row-total">{{ (item.price * item.selectedQuantity) | currency | currencySpacing }}</div>
                    <button type="button" class="hardware-remove" (click)="removeHardware(item)" [attr.aria-label]="'Remove ' + item.title"><i class="pi pi-trash"></i></button>
                </div>
            </div>

            <div class="section" *ngIf="selectedHardware.length && !hasBusinessAddress">
                <div class="section-head-row">
                    <h2>Business address</h2>
                    <span class="section-head-note">Required &middot; used to calculate tax</span>
                </div>
                <p class="section-subtitle">You haven't added a business address yet. We need it to work out the sales tax on this order, and it saves to your business information.</p>

                <form [formGroup]="businessAddressForm" class="shipping-form">
                    <div class="shipping-field">
                        <label for="biz-address">Street address <span class="req">*</span></label>
                        <input id="biz-address" type="text" pInputText placeholder="House number and street name" formControlName="address" />
                        <small class="shipping-error" *ngIf="baf.address.invalid && baf.address.touched">This field is required.</small>
                    </div>
                    <div class="shipping-field-row">
                        <div class="shipping-field">
                            <label for="biz-city">City <span class="req">*</span></label>
                            <input id="biz-city" type="text" pInputText placeholder="Enter your city" formControlName="city" />
                            <small class="shipping-error" *ngIf="baf.city.invalid && baf.city.touched">This field is required.</small>
                        </div>
                        <div class="shipping-field">
                            <label for="biz-state">State <span class="req">*</span></label>
                            <p-dropdown inputId="biz-state" [options]="usaStates" optionLabel="name" placeholder="Select a state" [filter]="true" filterBy="name" [(ngModel)]="selectedBusinessState" [ngModelOptions]="{ standalone: true }" appendTo="body" (onChange)="persistCartState()"></p-dropdown>
                        </div>
                        <div class="shipping-field">
                            <label for="biz-zip">Zip / Postal code <span class="req">*</span></label>
                            <input id="biz-zip" type="text" pInputText placeholder="Enter zip code" formControlName="zipCode" maxlength="10" />
                            <small class="shipping-error" *ngIf="baf.zipCode.invalid && baf.zipCode.touched">This field is required.</small>
                        </div>
                    </div>
                </form>
            </div>

            <div class="section" *ngIf="selectedHardware.length">
                <div class="section-head-row">
                    <h2>Shipping address</h2>
                </div>
                <div class="shipping-same-row">
                    <p-checkbox [binary]="true" [(ngModel)]="isBusinessAddressShipping" inputId="shipping-same" (onChange)="onShowShippingAddress($event)"></p-checkbox>
                    <label for="shipping-same">Same as business address</label>
                </div>
                <div class="shipping-business-address-line" *ngIf="isBusinessAddressShipping && hasBusinessAddress">{{ businessAddressOneLine }}</div>

                <form [formGroup]="shippingForm" class="shipping-form" *ngIf="!isBusinessAddressShipping">
                    <div class="shipping-field">
                        <label for="ship-address">Street address <span class="req">*</span></label>
                        <input id="ship-address" type="text" pInputText placeholder="House number and street name" formControlName="address" />
                        <small class="shipping-error" *ngIf="sf.address.invalid && sf.address.touched">This field is required.</small>
                    </div>
                    <div class="shipping-field">
                        <label for="ship-address2">Apartment, suite, unit, etc.</label>
                        <input id="ship-address2" type="text" pInputText placeholder="Apartment, suite, unit, building, floor, etc." formControlName="address2" />
                    </div>
                    <div class="shipping-field-row">
                        <div class="shipping-field">
                            <label for="ship-city">City <span class="req">*</span></label>
                            <input id="ship-city" type="text" pInputText placeholder="Enter your city" formControlName="city" />
                            <small class="shipping-error" *ngIf="sf.city.invalid && sf.city.touched">This field is required.</small>
                        </div>
                        <div class="shipping-field">
                            <label for="ship-state">State <span class="req">*</span></label>
                            <p-dropdown inputId="ship-state" [options]="usaStates" optionLabel="name" placeholder="Select a state" [filter]="true" filterBy="name" [(ngModel)]="selectedShippingState" [ngModelOptions]="{ standalone: true }" appendTo="body"></p-dropdown>
                        </div>
                        <div class="shipping-field">
                            <label for="ship-zip">Zip code <span class="req">*</span></label>
                            <input id="ship-zip" type="text" pInputText placeholder="Enter zip code" formControlName="zipCode" maxlength="10" />
                            <small class="shipping-error" *ngIf="sf.zipCode.invalid && sf.zipCode.touched">This field is required.</small>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <div class="checkout-summary">
            <h3>Order summary</h3>
            <div class="summary-sub">{{ billingInterval === "Yearly" ? "Annual" : "Monthly" }} subscription</div>
            <div class="summary-sub" *ngIf="hasAnyAlreadyPurchasedLine">Lines marked "already purchased" are already on your account — they're included so your Recurring total reflects your true bill, but you won't be charged for them again today.</div>

            <ng-container *ngIf="!hardwareOnlyCheckout">
            <div class="summary-block-label">SUBSCRIPTION</div>
            <div class="summary-line">
                <div>
                    <div class="summary-line-title">{{ plan?.planName }} plan</div>
                    <div class="summary-line-meta" *ngIf="isFreeTrialAccount && freeTrialEndDate">Free until {{ freeTrialEndDate | date : "MMM d, yyyy" }}</div>
                    <div class="summary-line-meta" *ngIf="isSwitchingPlan">Switching from {{ currentPlanName }}</div>
                </div>
                <div class="summary-line-amount">{{ planDisplayPrice | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "yr" : "mo" }}</div>
            </div>
            </ng-container>

            <ng-container *ngIf="!hardwareOnlyCheckout">
            <div class="summary-block-label" *ngIf="registerLocationExtraTotal > 0">LOCATIONS &amp; REGISTERS</div>
            <div class="summary-line" *ngFor="let item of registersAndLocationsList" [class.hidden]="extraCostFor(item) <= 0">
                <div>
                    <div class="summary-line-title">Additional {{ item.name.toLowerCase() }}</div>
                    <div class="summary-line-meta">Qty {{ item.quantity - (item.ownedQuantity ?? 0) }} &times; {{ (billingInterval === "Yearly" ? item.price * 12 : item.price) | currency | currencySpacing }}</div>
                </div>
                <div class="summary-line-amount">{{ extraCostFor(item) | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "yr" : "mo" }}</div>
            </div>

            <ng-container *ngFor="let item of registersAndLocationsList">
                <div class="summary-line" *ngIf="alreadyPurchasedExtra(item) as ownedExtra">
                    <div>
                        <div class="summary-line-title">{{ item.name }} &middot; already purchased</div>
                        <div class="summary-line-meta">Qty {{ ownedExtra }} &times; {{ (billingInterval === "Yearly" ? item.price * 12 : item.price) | currency | currencySpacing }}</div>
                    </div>
                    <div class="summary-line-amount">{{ (ownedExtra * (billingInterval === "Yearly" ? item.price * 12 : item.price)) | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "yr" : "mo" }}</div>
                </div>
            </ng-container>

            <div class="summary-block-label" *ngIf="hasSelectedAddons">ADD-ONS</div>
            <div class="summary-line" *ngFor="let addon of optionalAddons" [class.hidden]="!addon.selected">
                <div class="summary-line-title">{{ addon.planName }}</div>
                <div class="summary-line-amount">{{ (billingInterval === "Yearly" ? addon.yearlyPrice * 12 : addon.monthlyPrice) | currency | currencySpacing }}<ng-container *ngIf="addon.priceIsStartingAt">+</ng-container>/{{ billingInterval === "Yearly" ? "yr" : "mo" }}</div>
            </div>

            <div class="summary-block-label" *ngIf="alreadyPurchasedAddons.length">ADD-ONS &middot; ALREADY PURCHASED</div>
            <div class="summary-line" *ngFor="let addon of alreadyPurchasedAddons">
                <div class="summary-line-title">{{ addon.planName }}</div>
                <div class="summary-line-amount">{{ (billingInterval === "Yearly" ? addon.yearlyPrice * 12 : addon.monthlyPrice) | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "yr" : "mo" }}</div>
            </div>
            </ng-container>

            <div class="summary-total-row" *ngIf="!hardwareOnlyCheckout">
                <span>Recurring total</span>
                <strong>{{ recurringTotalDisplay | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "yr" : "mo" }}</strong>
            </div>

            <div class="summary-block-label" *ngIf="hardwareTotal > 0">HARDWARE (ONE-TIME)</div>
            <div class="summary-line" *ngFor="let item of selectedHardware">
                <div>
                    <div class="summary-line-title">{{ item.title }}</div>
                    <div class="summary-line-meta">Qty {{ item.selectedQuantity }} &times; {{ item.price | currency | currencySpacing }}</div>
                </div>
                <div class="summary-line-amount">{{ (item.price * item.selectedQuantity) | currency | currencySpacing }}</div>
            </div>

            <div class="coupon-block">
                <label for="coupon-code">Apply coupon</label>
                <div class="coupon-input-row">
                    <input id="coupon-code" type="text" [(ngModel)]="couponCode" placeholder="Enter coupon code" [disabled]="couponSuccess || isCouponValidating" />
                    <button type="button" (click)="applyCoupon()" [disabled]="!couponCode || isCouponValidating || couponSuccess">
                        {{ isCouponValidating ? "Checking…" : "Apply" }}
                    </button>
                </div>
                <small class="coupon-hint" *ngIf="!couponSuccess && !couponError">Only one coupon can be applied per checkout.</small>
                <small class="coupon-error" *ngIf="couponError">{{ validationMessage }}</small>
                <div class="coupon-applied" *ngIf="couponSuccess">
                    <span>{{ validationMessage }}</span>
                    <button type="button" (click)="clearCoupon()">Remove</button>
                </div>
            </div>

            <div class="summary-line">
                <span>Subtotal due today</span>
                <span>{{ subtotal | currency | currencySpacing }}</span>
            </div>
            <div class="summary-line">
                <div>
                    <div class="summary-line-title">Sales tax{{ taxRate > 0 ? " (" + taxRate + "%)" : "" }}</div>
                    <div class="summary-line-meta" *ngIf="taxRate > 0 && taxAmount === 0">{{ nextBillingTaxAmount | currency | currencySpacing }} on next billing cycle</div>
                </div>
                <span>{{ taxAmount | currency | currencySpacing }}</span>
            </div>

            <div class="amount-due-row">
                <span>Amount due today</span>
                <strong>{{ grandTotal | currency | currencySpacing }}</strong>
            </div>
            <p class="amount-due-note" *ngIf="hardwareOnlyCheckout">
                This one-time hardware charge is due today; your {{ plan?.planName }} plan and billing continue unchanged.
            </p>
            <!-- Hardware is always charged now; the plan/registers/locations/add-ons portion always
                 defers to the next billing cycle regardless of the account's own trial status — see
                 subtotal's doc comment. isFreeTrialAccount-only copy ("stays free until...") is kept
                 for an actual trial account since it's more specific than the generic "next billing
                 cycle" wording below. -->
            <p class="amount-due-note" *ngIf="isFreeTrialAccount && !hardwareOnlyCheckout">
                <ng-container *ngIf="grandTotal === 0">Nothing to pay today. </ng-container>
                <ng-container *ngIf="isUsageOnlyCheckout">These additions ride along on your free period<ng-container *ngIf="freeTrialEndDate"> until {{ freeTrialEndDate | date : "MMM d, yyyy" }}</ng-container>, then your plan and these additions bill <span class="amount-due-highlight">{{ recurringTotalDisplayWithTax | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "year" : "month" }}</span><ng-container *ngIf="taxRate > 0"> (incl. tax)</ng-container> together.</ng-container>
                <ng-container *ngIf="!isUsageOnlyCheckout">Your {{ plan?.planName }} plan stays free<ng-container *ngIf="freeTrialEndDate"> until {{ freeTrialEndDate | date : "MMM d, yyyy" }}</ng-container>, then bills <span class="amount-due-highlight">{{ recurringTotalDisplayWithTax | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "year" : "month" }}</span><ng-container *ngIf="taxRate > 0"> (incl. tax)</ng-container>.</ng-container>
            </p>
            <p class="amount-due-note" *ngIf="!isFreeTrialAccount && !hardwareOnlyCheckout">
                <ng-container *ngIf="grandTotal === 0">Nothing to pay today. </ng-container>
                <ng-container *ngIf="grandTotal > 0">Your card is charged {{ grandTotal | currency | currencySpacing }} today for the hardware selected above. </ng-container>
                <ng-container *ngIf="recurringTotalDisplayWithTax > 0">Your {{ plan?.planName }} plan, locations/registers and add-ons bill <span class="amount-due-highlight">{{ recurringTotalDisplayWithTax | currency | currencySpacing }}/{{ billingInterval === "Yearly" ? "year" : "month" }}</span><ng-container *ngIf="taxRate > 0"> (incl. tax)</ng-container> on your next billing cycle<ng-container *ngIf="nextBillingDate"> ({{ nextBillingDate | date : "MMM d, yyyy" }})</ng-container>.</ng-container>
            </p>

            <div class="terms-row">
                <p-checkbox [binary]="true" [(ngModel)]="terms"></p-checkbox>
                <span>I agree to the <a href="https://lincsell.com/terms-of-service/" target="_blank">terms</a> and <a href="https://lincsell.com/privacy-policy/" target="_blank">privacy policy</a>.</span>
            </div>

            <p class="usage-selection-hint" *ngIf="isUsageOnlyCheckout && !hasAnyUsageSelection">
                <i class="pi pi-info-circle"></i>
                <ng-container *ngIf="hardwareOnlyCheckout; else usageHintDefault">Add at least one item above to continue.</ng-container>
                <ng-template #usageHintDefault>Your {{ plan?.planName }} plan is already active — add a register, location or add-on above to continue.</ng-template>
            </p>

            <!-- Covers every other canSubmit block (terms, missing address) so the disabled Confirm
                 button always has a visible reason, not just a hover-only title tooltip that touch
                 devices can't reach. -->
            <p class="usage-selection-hint" *ngIf="!canSubmit && submitTitle && !(isUsageOnlyCheckout && !hasAnyUsageSelection)">
                <i class="pi pi-info-circle"></i>
                {{ submitTitle }}
            </p>

            <button pButton type="button" [label]="submitLabel" class="confirm-btn" [loading]="isSubmitting" [disabled]="!canSubmit" [title]="submitTitle" (click)="confirm()"></button>
        </div>
    </div>

    </ng-container>

    <ng-template #pageSkeleton>
        <div class="checkout-body">
            <div class="checkout-main">
                <p-skeleton width="40%" height="2rem" styleClass="mb-3"></p-skeleton>
                <p-skeleton width="70%" height="1rem" styleClass="mb-4"></p-skeleton>
                <p-skeleton width="100%" height="4rem" styleClass="mb-4"></p-skeleton>
                <p-skeleton width="100%" height="8rem" styleClass="mb-4"></p-skeleton>
                <p-skeleton width="100%" height="8rem"></p-skeleton>
            </div>
            <div class="checkout-summary">
                <p-skeleton width="60%" height="1.5rem" styleClass="mb-3"></p-skeleton>
                <p-skeleton width="100%" height="10rem" styleClass="mb-3"></p-skeleton>
                <p-skeleton width="100%" height="3rem"></p-skeleton>
            </div>
        </div>
    </ng-template>
</div>

<!-- Hardware picker -->
<p-dialog
    header="Hardware &amp; Accessories"
    [(visible)]="hardwareDialogVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [closable]="true"
    (onHide)="cancelHardwareSelection()"
    [style]="{ width: '70vw', maxWidth: '1100px' }"
    [breakpoints]="{ '1199px': '85vw', '768px': '95vw', '575px': '96vw' }"
    [contentStyle]="{ 'max-height': '70vh', overflow: 'auto' }"
>
    <div *ngIf="!loadingHardwares; else hardwareLoading">
        <div class="hardware-picker-category" *ngFor="let category of hardwareCategories">
            <h3 class="hardware-picker-category-title">{{ category.title }}</h3>
            <div class="hardware-picker-grid">
                <div class="hardware-card" *ngFor="let item of category.items">
                    <img [src]="item.image" alt="" class="hardware-card-img" onerror="this.style.visibility='hidden'" />
                    <div class="hardware-card-title">{{ item.title }}</div>
                    <div class="hardware-card-price">{{ item.price | currency | currencySpacing }}</div>
                    <div class="hardware-card-stepper">
                        <button pButton type="button" icon="pi pi-minus" class="p-button-text p-button-sm" (click)="decrementHardwareQty(item)" [attr.aria-label]="'Decrease quantity of ' + item.title"></button>
                        <span aria-live="polite">{{ item.selectedTempQuantity }}</span>
                        <button pButton type="button" icon="pi pi-plus" class="p-button-text p-button-sm" (click)="incrementHardwareQty(item)" [attr.aria-label]="'Increase quantity of ' + item.title"></button>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <ng-template #hardwareLoading>
        <div class="hardware-picker-grid">
            <p-skeleton *ngFor="let row of [1,2,3,4]" width="100%" height="14rem"></p-skeleton>
        </div>
    </ng-template>

    <ng-template pTemplate="footer">
        <button pButton type="button" label="Close" class="p-button-outlined" (click)="cancelHardwareSelection()"></button>
        <button pButton type="button" label="Save and Close" [disabled]="!isAnyHardwareInPopupSelected" (click)="saveHardwareSelection()"></button>
    </ng-template>
</p-dialog>

<!-- Leave-with-unsaved-changes confirm (Back button / browser Back / Forward) -->
<p-dialog
    header="Leave checkout?"
    [(visible)]="showLeaveConfirm"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    [closable]="false"
    [style]="{ width: '440px', maxWidth: '92vw' }"
>
    <p>You haven't confirmed this order yet. If you leave now, nothing will be charged and this order won't be placed.</p>
    <ng-template pTemplate="footer">
        <button pButton type="button" label="Stay on this page" class="p-button-outlined" (click)="confirmLeave(false)"></button>
        <button pButton type="button" label="Leave without ordering" (click)="confirmLeave(true)"></button>
    </ng-template>
</p-dialog>
